<template>
  <ui-form class="sms-confirmation-with-cf" @submit="submit">
    <span v-if="readOnly" class="read-only-number">
      <span>{{ $t('App.Sms.PhoneNumber') }}:</span>
      +{{ phoneNumberDetails.number }}
    </span>
    <phone-input
      v-else
      label="App.Sms.PhoneNumber"
      :value="phoneNumberDetails"
      :error="error"
      :country="country"
      :disabled="isSmsSubmitted"
      @change="phoneNumberChange"
    />
    <div
      v-if="showTurnstile"
      id="turnstile-container"
      class="cloudflare-turnstile"
    />
    <div class="controls-container">
      <ui-button
        v-if="!isSmsSubmitted"
        :label="`${resource}.Send`"
        :disabled="isLoading || showTurnstile"
      />
      <template v-else>
        <ui-button
          :label="`${resource}.Resend`"
          :disabled="isLoading || showTurnstile"
        />
        <ui-button
          v-if="!readOnly"
          :label="`${resource}.Change`"
          type="button"
          additional
          :disabled="isLoading || showTurnstile"
          @click="changeNumber"
        />
      </template>
    </div>
  </ui-form>
</template>

<script>
import PhoneInput from '@src/components/partials/PhoneInput'
import Validators from '@src/scripts/validators'

export default {
  components: {
    PhoneInput
  },

  props: {
    resource: { type: String, default: 'App.Sms' },
    country: { type: String, default: undefined },
    phoneNumber: { type: Object, default: undefined },
    readOnly: Boolean
  },

  data() {
    return {
      isLoading: false,
      isSmsSubmitted: false,
      phoneNumberDetails: {
        code: '',
        number: ''
      },
      error: null,
      showTurnstile: false,
      turnstileToken: null
    }
  },

  mounted() {
    if (!document.getElementById('turnstile-script')) {
      const script = document.createElement('script')
      script.id = 'turnstile-script'
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
    }
  },

  created() {
    const { phoneNumber, phoneNumberDetails } = this

    if (!phoneNumber) {
      return
    }

    phoneNumberDetails.number = phoneNumber.number || ''
    phoneNumberDetails.code = phoneNumber.code || ''
  },

  methods: {
    phoneNumberChange(value) {
      this.phoneNumberDetails = value
    },

    changeNumber() {
      this.isSmsSubmitted = false
      this.$emit('restart')
    },

    validate(phoneNumberDetails) {
      const error = Validators.phoneNumberValidator(phoneNumberDetails)
      this.error = error ? `App.Sms.Error.${error}` : null
      return !this.error
    },

    submit() {
      const { phoneNumberDetails, readOnly } = this
      if (!readOnly && !this.validate(phoneNumberDetails)) {
        return
      }

      // If no token is available, show turnstile container which generated new token
      if (!this.turnstileToken) {
        this.showTurnstile = true
        this.renderTurnstile()
        return
      }

      this.isLoading = true
      this.isSmsSubmitted = true

      this.$emit(
        'complete',
        phoneNumberDetails,
        (isSmsSubmissionSuccess) => {
          this.isLoading = false

          if (!isSmsSubmissionSuccess) {
            this.isSmsSubmitted = false
          }

          this.turnstileToken = null
          this.showTurnstile = false
        },
        this.turnstileToken
      )
    },

    renderTurnstile() {
      this.$nextTick(() => {
        // Clear any previous content to force a fresh render for resend functionality
        if (this.widgetId) {
          window.turnstile.remove(this.widgetId)
          this.widgetId = null
        }

        this.widgetId = window?.turnstile?.render('#turnstile-container', {
          sitekey: TurnstileKey,
          theme: 'light',
          callback: (token) => {
            this.turnstileToken = token
            this.submit()
          },
          'expired-callback': () => {
            this.turnstileToken = null
          },
          'error-callback': () => {
            this.turnstileToken = null
          }
        })
      })
    }
  }
}
</script>

<style scoped>
.sms-confirmation {
  margin-top: 2rem;
  width: 100%;
}

.cloudflare-turnstile {
  margin-top: 1rem;
}

.controls-container {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.controls-container .ui-button {
  margin: 0 1rem;
}

.read-only-number,
.read-only-number > span {
  font-size: 1rem;
  text-align: center;
  color: var(--text-contrast-color);
}

.read-only-number {
  display: block;
}

.read-only-number > span {
  font-weight: 500;
}
</style>
